import { ScrollArea, Table } from '@mantine/core';
import {
    Row,
    TableOptions,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import cx from 'classnames';

import EmptyState from 'Components/Common/EmptyState/EmptyState';

import styles from './ConfigurableTanstackTable.module.scss';

type Props<TData extends object> = {
    data: TData[];
    columns: TableOptions<TData>['columns'];
    getRowId?: TableOptions<TData>['getRowId'];
    className?: string;
    tableClassName?: string;
    rowClassName?: (row: Row<TData>) => string | undefined;
    emptyStateTitle?: string;
};

const ConfigurableTanstackTable = <TData extends object>({
    data,
    columns,
    getRowId,
    className,
    tableClassName,
    rowClassName,
    emptyStateTitle,
}: Props<TData>) => {
    const table = useReactTable({
        data,
        columns,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
    });

    if (table.getRowModel().rows.length === 0 && emptyStateTitle) {
        return <div className={cx(styles.emptyContainer, className)}><EmptyState title={emptyStateTitle} /></div>;
    }

    return (
        <ScrollArea className={cx(styles.wrapper, className)}>
            <Table withRowBorders withColumnBorders highlightOnHover className={cx(styles.table, tableClassName)}>
                <Table.Thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <Table.Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <Table.Th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </Table.Th>
                            ))}
                        </Table.Tr>
                    ))}
                </Table.Thead>
                <Table.Tbody>
                    {table.getRowModel().rows.map((row) => (
                        <Table.Tr key={row.id} className={rowClassName?.(row)}>
                            {row.getVisibleCells().map((cell) => (
                                <Table.Td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </Table.Td>
                            ))}
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </ScrollArea>
    );
};

export default ConfigurableTanstackTable;
